<script lang="ts" setup>
import { computed, inject } from 'vue';
import { useTheme } from 'vuetify';
import { ROUTE_NAMES } from '@/router/conf';
import useAuthenticatedQuery from '@/composables/apiServices/useAuthenticatedQuery';
import { SIDEBAR_MENU_OPEN_INJECT_KEY } from '@/types/components/layouts/BaseLayout.consts';

const siteTitle = import.meta.env.VITE_WEBMAIL_TITLE;

const theme = useTheme();

const { userIndex, queriesEnabled } = useAuthenticatedQuery();
const sidebarMenuOpenInjectedProps = inject(SIDEBAR_MENU_OPEN_INJECT_KEY);

const props = defineProps<{
  logoColor?:
    | 'primary'
    | 'on-background'
    | 'on-surface'
    | 'on-primary'
    | 'on-secondary'
    | 'on-success'
    | 'on-warning'
    | 'on-error'
    | 'on-alert'
    | 'on-info';
}>();

const logoColor = computed(() => theme.current.value.colors[props.logoColor ?? 'logo']?.toString());

const currentThemeName = computed(() => theme.global.name.value.toUpperCase().replace('-', '_'));

const customLogoUrl = computed(() => {
  // Check for theme-specific logo
  const themeSpecificEnvVar = `VITE_LOGO_${currentThemeName.value}`;
  let logoUrl = import.meta.env[themeSpecificEnvVar];

  // If not found, and it's a dark theme, check for global dark theme logo
  if (!logoUrl && currentThemeName.value.includes('_DARK')) {
    logoUrl = import.meta.env.VITE_LOGO_DARK;
  }

  // If still not found, check for global light theme logo
  if (!logoUrl) {
    logoUrl = import.meta.env.VITE_LOGO;
  }

  // If still not found, return an empty string to use the default logo
  return logoUrl || '';
});
const handleLogoClick = () => {
  if (sidebarMenuOpenInjectedProps) {
    sidebarMenuOpenInjectedProps.setNewValue(false);
  }
};
</script>

<template>
  <router-link
    :to="queriesEnabled ? { name: ROUTE_NAMES.BASE_LAYOUT, params: { userIndex } } : ''"
    @click="handleLogoClick"
  >
    <svg
      v-if="!customLogoUrl"
      aria-labelledby="logo-title"
      fill="none"
      height="23"
      role="img"
      viewBox="30 8 90 21"
      width="120"
      xmlns="http://www.w3.org/2000/svg"
    >
      <title id="logo-title" lang="en">{{ siteTitle }} logo</title>
      <g clip-path="url(#clip0_54697_62022)">
        <!-- Rip Pirgit 2025 -->
        <!-- <path
          :fill="logoColor"
          d="M14.4325 28.2753L7.53143 15.1034L10.7971 13.3412L17.6983 26.5128L14.4325 28.2753ZM8.41562 18.7999L0 20.4604L0.705866 24.1453L9.12131 22.4848L8.41562 18.7999ZM20.7611 10.5164L2.60713 6.93563L1.90126 10.6202L20.0554 14.2014L20.7611 10.5164ZM15.7585 4.09503C15.7585 2.30692 14.3302 0.857422 12.568 0.857422C10.8064 0.857422 9.37826 2.30692 9.37826 4.09503C9.37826 5.88279 10.8064 7.33264 12.568 7.33264C14.3302 7.33264 15.7585 5.88279 15.7585 4.09503Z"
        /> -->
        <path
          :fill="logoColor"
          d="M49.2298 17.2799L33.4139 22.952H51.8246V26.7646H34.9272C32.7187 26.7646 30.9424 24.9377 30.9424 22.6656C30.9424 20.7277 32.286 19.1708 34.0313 18.7097L49.863 13.038H31.8693V9.22559H48.3338C50.5427 9.22559 52.3188 11.0528 52.3188 13.3245C52.3188 15.2622 50.9748 16.8196 49.2298 17.2799ZM71.8429 22.9599H58.6637V13.0403H71.8429V22.9599ZM75.6022 14.376C75.6022 11.5301 73.3308 9.22559 70.527 9.22559H59.9802C57.1757 9.22559 54.9047 11.5303 54.9047 14.376V21.6245C54.9047 24.47 57.1757 26.7749 59.9802 26.7749H70.527C73.331 26.7749 75.6022 24.4699 75.6022 21.6245V14.376ZM98.3435 14.376C98.3435 11.5308 96.0721 9.22559 93.2684 9.22559H78.5444V26.7749H82.3037V13.041H94.5839V26.7749H98.3435V14.376ZM120 22.9595H104.859V19.908H120V16.0925H104.859V13.041H120V9.22559H106.174C103.371 9.22559 101.1 11.5308 101.1 14.376V21.6245C101.1 24.4697 103.371 26.7749 106.174 26.7749H120V22.9595Z"
        />
      </g>
      <defs>
        <clipPath id="clip0_54697_62022">
          <rect :fill="logoColor" height="34.2857" transform="translate(0 0.857422)" width="120" />
        </clipPath>
      </defs>
    </svg>

    <img v-else :alt="`${siteTitle} logo`" :src="customLogoUrl" height="36" width="120" />
  </router-link>
</template>
